import {
  useGetEnrollmentMultiWireTransferAccountData,
  useGetEnrollmentMultiWireTransferData,
  useSubmitEnrollmentMultiFundingData,
} from '@egw/api'
import { Button, ButtonVariant, GenericError } from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid'
import cn from 'classnames'
import { useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { PageHeader } from '../../../components'
import { GeneratePDFDownload } from '../../../components/generate-pdf/generate-pdf'
import { type Config } from '../../../config/config'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'

const log = new Logger('Enrollment - Wire Transfer')

export const WireTransferPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const config = configClient.getConfig() as Config

  const [showAccountNumber, setShowAccountNumber] = useState<boolean>(false)

  const toggleAccountNumber = (): void => {
    setShowAccountNumber((toggle) => !toggle)
  }

  const handler = (): void => {
    try {
      GeneratePDFDownload({
        routingNumber,
        accountNumber,
        addressLine1,
        addressLine2,
        addressCity,
        addressRegionCode,
        addressPostalCode,
        firstName,
        lastName,
        t,
      })
    } catch (err: unknown) {
      log.error('Error generating PDF', err)
    }
  }

  const {
    data: accountData,
    error: accountDataError,
    isLoading: accountDataLoading,
  } = useGetEnrollmentMultiWireTransferAccountData()

  const { data, isLoading, error } = useGetEnrollmentMultiWireTransferData()

  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentMultiFundingData()

  const routingNumber = accountData?.highYieldChecking.accounts[0].routingNumber
  const accountNumber = accountData?.highYieldChecking.accounts[0].accountNumber
  const addressLine1 = data?.enrollmentData?.addressLine1
  const addressLine2 = data?.enrollmentData?.addressLine2
  const addressCity = data?.enrollmentData?.addressCity
  const addressRegionCode = data?.enrollmentData?.addressRegionCode
  const addressPostalCode = data?.enrollmentData?.addressPostalCode
  const firstName = data?.enrollmentData?.firstName
  const lastName = data?.enrollmentData?.lastName

  const fundAnotherWayHandler = (): void => {
    void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.FUNDING.path}`)
  }

  const continueToPasskeyHandler = async (): Promise<void> => {
    const response = await mutate({ skipFunding: true })

    if (response?.path) {
      void navigate(response.path)
    } else {
      void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
    }
  }

  return (
    <EnrollmentMultiLayout title={t('multi.wire_transfer.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading || accountDataLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !accountDataLoading && !error && !accountDataError ? (
        <>
          {/* AUTO GENERATED COMPONENTS START*/}
          <PageHeader className="!mb-2xl" title={t('multi.wire_transfer.title')} />
          <div className="flex flex-col mb-2xl">
            <p className="typo-primary-lg text-text-header mb-md">{t('multi.wire_transfer.subtitle')}</p>
            <p className="typo-primary-base text-text-meta">{t('multi.wire_transfer.description')}</p>
            <div className="flex flex-col mt-2xl gap-md">
              {/* Bank */}
              <div className="flex flex-row gap-sm">
                <p className="typo-primary-base text-text-meta w-1/3">{t('multi.wire_transfer.bank_label')}</p>
                <div className="w-2/3">
                  <p className="typo-primary-base-semibold text-text-header w-2/3 inline">
                    {t('multi.wire_transfer.coastal_community_bank')}
                  </p>
                </div>
              </div>

              {/* Routing Number */}
              <div className="flex flex-row gap-sm">
                <p className="typo-primary-base text-text-meta w-1/3">
                  {t('multi.wire_transfer.routing_number_label')}
                </p>
                <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">{routingNumber}</p>
              </div>
              {/* Account Number */}
              <div className="flex flex-row gap-sm">
                <p className="typo-primary-base text-text-meta w-1/3">
                  {t('multi.wire_transfer.account_number_label')}
                </p>
                <div className="flex flex-row gap-xs items-center">
                  <p
                    className={cn('typo-primary-base-semibold text-text-header w-2/3 fs-exclude', {
                      'tracking-widest': !showAccountNumber,
                    })}
                  >
                    {showAccountNumber ? accountNumber : '••••••••••'}
                  </p>
                  {showAccountNumber ? (
                    <EyeSlashIcon onClick={toggleAccountNumber} className="w-4 h-4 fill-icons-muted cursor-pointer" />
                  ) : (
                    <EyeIcon onClick={toggleAccountNumber} className="w-4 h-4 fill-icons-muted cursor-pointer" />
                  )}
                </div>
              </div>
              {/* Beneficiary Name */}
              <div className="flex flex-row gap-sm">
                <p className="typo-primary-base text-text-meta w-1/3">{t('multi.wire_transfer.beneficiary_label')}</p>
                <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">
                  {firstName} {lastName}
                </p>
              </div>
              {/* Beneficiary Address */}
              <div className="flex flex-row gap-sm">
                <p className="typo-primary-base text-text-meta w-1/3">
                  {t('multi.wire_transfer.beneficiary_address_label')}
                </p>
                <p className="typo-primary-base-semibold text-text-header w-2/3 fs-exclude">
                  {addressLine1 ? <p>{addressLine1}</p> : null}
                  {addressLine2 ? <p>{addressLine2}</p> : null}
                  <div className="flex flex-row">
                    {addressCity ? <span>{addressCity} &nbsp;</span> : null}
                    {addressRegionCode ? <span>{addressRegionCode}, &nbsp;</span> : null}
                    {addressPostalCode ? <span>{addressPostalCode}</span> : null}
                  </div>
                </p>
              </div>
              {/* Bank Address */}
              <div className="flex flex-row gap-sm fs-exclude">
                <p className="typo-primary-base text-text-meta w-1/3">{t('multi.wire_transfer.bank_address_label')}</p>
                <p className="typo-primary-base-semibold text-text-header w-2/3">
                  {t('multi.wire_transfer.bank_address')}
                  <br />
                  {t('multi.wire_transfer.bank_address_2')}
                </p>
              </div>
              {/* Memo */}
              <div className="flex flex-row gap-sm fs-exclude">
                <p className="typo-primary-base text-text-meta w-1/3">{t('multi.wire_transfer.memo')}</p>
                <p className="typo-primary-base-semibold text-text-header w-2/3 italic">
                  {t('multi.wire_transfer.enter_as_needed')}
                </p>
              </div>

              <Button
                variant={ButtonVariant.Secondary}
                className="sm:w-fit w-full mb-2xs typo-primary-base"
                onClick={handler}
              >
                {t('multi.wire_transfer.download_wire_instructions')}
              </Button>
            </div>
            <p className="typo-primary-sm-light text-text-meta">{t('multi.wire_transfer.disclaimer')}</p>
          </div>
          <div className="flex flex-grow md:hidden" />
          <div className="flex flex-col sm:flex-row gap-md w-full">
            <Button variant={ButtonVariant.Secondary} className="w-full sm:w-fit" onClick={fundAnotherWayHandler}>
              {t('multi.wire_transfer.fund_another_way')}
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              className="w-full sm:w-fit"
              onClick={() => void continueToPasskeyHandler()}
              isLoading={isSubmitting}
            >
              {t('multi.wire_transfer.continue_to_passkey')}
            </Button>
          </div>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? accountDataError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
